import { SendOutlined, StopOutlined } from "@ant-design/icons";
import { Alert, Button, Drawer, Input, Modal, Space, Tag } from "antd";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { HelpNoticeDetails } from "./HelpNoticeDetails";
import { HelpAnswer } from "./HelpAnswer";
import { sectionFor } from "./screen";
import { codepoints } from "./chatHistory";
import { useManualGeneration, useManualOwner, useManualIndex } from "./manualHooks";
import type { HelpStatus } from "./chatTypes";
import type { useHelpChat } from "./useHelpChat";

export function HelpChatDrawer({ open, onClose, status, chat }: { open: boolean; onClose: () => void;
  status?: HelpStatus; chat: ReturnType<typeof useHelpChat> }) {
  const [question, setQuestion] = useState("");
  const [details, setDetails] = useState(false);
  const [removedScreen, setRemovedScreen] = useState<string | null>(null);
  const location = useLocation();
  const [iss, sub] = useManualOwner();
  const generation = useManualGeneration();
  const index = useManualIndex();
  const hit = sectionFor(index.data, location.pathname);
  const screen = hit?.id !== removedScreen ? hit : undefined;
  const [wide, setWide] = useState(() => window.matchMedia("(min-width: 1280px)").matches);
  useEffect(() => {
    const query = window.matchMedia("(min-width: 1280px)");
    const resize = () => setWide(query.matches);
    query.addEventListener("change", resize);
    resize();
    return () => query.removeEventListener("change", resize);
  }, []);
  useEffect(() => { setQuestion(""); setRemovedScreen(null); }, [iss, sub, generation, status?.view]);
  useEffect(() => {
    const discard = () => setQuestion("");
    window.addEventListener("itmgmt-signout", discard);
    return () => window.removeEventListener("itmgmt-signout", discard);
  }, []);
  const [retryClock, setRetryClock] = useState(Date.now());
  useEffect(() => {
    const next = Math.min(...chat.turns.map((turn) => turn.retryAt ?? 0).filter((at) => at > Date.now()));
    if (Number.isFinite(next)) {
      const timer = setTimeout(() => setRetryClock(Date.now()), Math.max(0, next - Date.now()) + 10);
      return () => clearTimeout(timer);
    }
  }, [chat.turns, retryClock]);
  const send = () => { if (question.trim() && codepoints(question) <= 1000 && status?.available && !chat.streaming) {
    void chat.send(question, screen?.id ?? null); setQuestion("");
  } };
  return <Drawer open={open} onClose={onClose} placement="right" width={wide ? 440 : "100%"}
    title="ヘルプチャット" extra={<Button onClick={() => { chat.clear(); setQuestion(""); }}>会話を消す</Button>}>
    <small>{status?.notice}</small> <Button type="link" size="small" onClick={() => setDetails(true)}>詳しく</Button>
    {!status?.available && <Alert type="warning" message={status?.message ?? "状態を確認しています"} />}
    <p>回答には誤りがありえます。操作の前にリンク先の節で確かめてください。</p>
    <div aria-live="polite" className="help-messages">
      {chat.turns.map((turn, i) => <div key={turn.id} style={{ marginBottom: 20 }}>
        <p style={{ textAlign: "right", whiteSpace: "pre-wrap" }} data-testid="help-question">{turn.question}</p>
        <HelpAnswer text={turn.answer} done={turn.done} streaming={chat.streaming && i === chat.turns.length - 1} />
        {turn.incomplete && !(chat.streaming && i === chat.turns.length - 1) && <Tag>未完了</Tag>}
        {turn.done && turn.done.stop !== "end" && <small>この回答は次の質問の文脈に含めません</small>}
        {turn.error && <Alert type="error" message={turn.error} action={turn.retryable && !chat.streaming ?
          <Button disabled={!!turn.retryAfter && retryClock < (turn.retryAt ?? 0)}
            onClick={() => void chat.send(turn.question, turn.snapshot.screen, turn.snapshot)}>再試行</Button> : undefined} />}
      </div>)}
    </div>
    {screen && <Tag closable onClose={() => setRemovedScreen(screen.id)}>この画面について: {screen.title}</Tag>}
    <Input.TextArea aria-label="質問" value={question} autoSize={{ minRows: 1, maxRows: 6 }}
      count={{ show: true, max: 1000, strategy: codepoints }}
      onChange={(e) => setQuestion(Array.from(e.target.value).slice(0, 1000).join(""))}
      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) {
        e.preventDefault(); send();
      } }} />
    <Space style={{ marginTop: 8 }}>
      <Button type="primary" icon={chat.streaming ? <StopOutlined /> : <SendOutlined />}
        disabled={!chat.streaming && (!status?.available || !question.trim())}
        onClick={chat.streaming ? chat.stop : send}>{chat.streaming ? "停止" : "送信"}</Button>
      <span style={{ color: status && status.daily_cap - status.used_today <= 5 ? "#ad6800" : undefined }}>
        {status?.used_today ?? 0} / {status?.daily_cap ?? 0} 回</span>
    </Space>
    {chat.trimmed && <small>古い会話を省きました</small>}
    <Modal open={details} title="外部送信と保持について" onCancel={() => setDetails(false)} footer={null}>
      <HelpNoticeDetails status={status} />
    </Modal>
  </Drawer>;
}
